import React from 'react';

import type * as api from '@gitbook/api-client';
import type * as analytics from '@gitbook/app-analytics/client';
import type { NavigationScreenSpaceState } from '@gitbook/app-navigation';
import { Icon } from '@gitbook/spine-icons';
import {
    BreadcrumbsSkeleton,
    Button,
    HeaderActionsSeparator,
    HeaderBar,
    HeaderBarActions,
    HeaderBarActionsMain,
    HeaderBarActionsSide,
    HeaderBarBreadcrumbs,
    HeaderBarMain,
    HeaderBarTabs,
} from '@gitbook/spine-react';

import { ChangeRequestsButton } from '../ChangeRequest';
import { CommentsSideSheetButton } from '../Comments';
import { TableOfContentsMobileToggleButton } from '../TableOfContents';
import { BreadcrumbsSpace } from './BreadcrumbsSpace';
// import { BrokenLinksButton } from './BrokenLinksButton';
import { EditSpaceButton } from './EditSpaceButton';
import { GitSyncStateButton } from './GitSyncStateButton';
import { SpaceCommandsButton } from './SpaceCommandsButton';
import { SpaceHistoryButton } from './SpaceHistoryButton';
import { SpaceIntegrationsButton } from './SpaceIntegrationsButton';
import { SpaceRealtimeParticipants } from './SpaceRealtimeParticipants';
import { ViewModeButton } from './ViewModeButton';

/**
 * Header for a space.
 */
export function HeaderSpacePrimary(props: {
    screen: NavigationScreenSpaceState;
    space: api.Space;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
}) {
    const { screen, space, analyticsContext } = props;

    return (
        <HeaderBar>
            <HeaderBarMain>
                <TableOfContentsMobileToggleButton />

                <HeaderBarBreadcrumbs>
                    <React.Suspense fallback={<BreadcrumbsSkeleton items={2} />}>
                        <BreadcrumbsSpace space={space} />
                    </React.Suspense>
                </HeaderBarBreadcrumbs>

                <HeaderBarTabs>
                    <ViewModeButton screen={screen} space={space} changeRequest={null} />
                    <SpaceCommandsButton
                        space={space}
                        screen={screen}
                        analyticsContext={analyticsContext}
                    />
                </HeaderBarTabs>
            </HeaderBarMain>
            <HeaderBarActions>
                <HeaderBarActionsSide>
                    {screen.sideSheet ? null : (
                        <>
                            <SpaceRealtimeParticipants />

                            {/* Git Sync */}
                            {space.permissions.admin ? (
                                <GitSyncStateButton iconOnly="max-xl" space={space} />
                            ) : null}

                            {/* Share */}
                            {space.permissions.admin ? (
                                <Button
                                    label="Share"
                                    kind="blank"
                                    leadingIcon={Icon.UserAdd}
                                    title="Invite teammates to collaborate"
                                    iconOnly="max-xl"
                                    href={{
                                        current: {
                                            sideSheet: {
                                                type: 'share',
                                                tab: 'invite',
                                            },
                                        },
                                    }}
                                />
                            ) : null}

                            {/* Integrations */}
                            <SpaceIntegrationsButton space={space} />
                            <HeaderActionsSeparator />
                        </>
                    )}
                    {/* Comments */}
                    {space.permissions.comment ? (
                        <CommentsSideSheetButton
                            kind="blank"
                            size="medium"
                            space={space}
                            changeRequest={null}
                            screen={screen}
                            title="View all comments in this space"
                        />
                    ) : null}

                    {/* Audits */}
                    {/* <BrokenLinksButton space={space} changeRequest={null} /> */}

                    {/* History */}
                    <SpaceHistoryButton space={space} changeRequest={null} />
                </HeaderBarActionsSide>
                <HeaderBarActionsMain>
                    {/* Change Requests */}
                    {
                        // https://linear.app/gitbook-x/issue/RND-1988/explore-allowing-readers-to-access-history-and-change-requests
                        space.permissions.edit ? (
                            <ChangeRequestsButton
                                kind="secondary"
                                space={space}
                                screen={screen}
                                leadingIcon={Icon.ChangeRequest}
                                title={'Review, suggest and view\nexisting change requests'}
                                label="Change requests"
                                iconOnly="max-lg"
                            />
                        ) : null
                    }

                    {/* Edit */}
                    {!screen.changeRequest && !screen.revision && space.permissions.edit ? (
                        <EditSpaceButton space={space} analyticsContext={analyticsContext} />
                    ) : null}
                </HeaderBarActionsMain>
            </HeaderBarActions>
        </HeaderBar>
    );
}
